import React, { useMemo, useState } from 'react';
import { useAtom } from 'jotai/index';
import useInputState from '../../hooks/useInputState';
import Form, { FORM_BUTTON_TYPES, FORM_INPUT_TYPES } from '../../ui-kit/Form';
import BuildingImage from '../../assets/images/Building.png';
import useTranslations from '../../hooks/useTranslations';
import { DICTIONARY } from '../../translations/dictionary';
import { feedbackTopicsAtoms } from '../../atoms/feedbackAtoms';
import { closeModalAtom, modalStateAtom, openInfoModalAtom } from '../../atoms/modalAtoms';
import sleep from '../../helpers/sleep';
import { isAuthAtom } from '../../atoms/accountAtoms';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../api';
import { windowWidthAtom } from '../../atoms/globalAtoms';
import PersonalDataProcessingModal from './components/PersonalDataProcessingModal';

function QuestionsForm({ ...p } = {}) {
  const { t, lang } = useTranslations();
  const [name, setName, nameError, setNameError] = useInputState('');
  const [email, setEmail, emailError, setEmailError] = useInputState('');
  const [topic, setTopic, topicError, setTopicError] = useInputState();
  const [request, setRequest, requestError, setRequestError] = useInputState('');
  const [checkboxValue, setCheckboxValue, checkboxValueError, setCheckboxValueError] = useInputState(false);

  const [loading, setLoading] = useState(false);
  const [isAuth] = useAtom(isAuthAtom);
  const [feedbackTopics] = useAtom(feedbackTopicsAtoms);
  const [windowWidth] = useAtom(windowWidthAtom);
  const [, setModalState] = useAtom(modalStateAtom);
  const [, openInfoModal] = useAtom(openInfoModalAtom);
  const [, closeModal] = useAtom(closeModalAtom);

  const onSubmit = (ev) => {
    ev.preventDefault();
    let hasError = false;
    if (!isAuth && !name) {
      setNameError(t(DICTIONARY.nameRequired));
      hasError = true;
    }
    if (!isAuth && !email) {
      setEmailError(t(DICTIONARY.emailRequired));
      hasError = true;
    }
    if (!topic) {
      setTopicError(t(DICTIONARY.topicRequired));
      hasError = true;
    }
    if (!request) {
      setRequestError(t(DICTIONARY.requestRequired));
      hasError = true;
    }
    if (!checkboxValue) {
      setCheckboxValueError(t(DICTIONARY.policyRequired));
      hasError = true;
    }
    if (!hasError) sendFeedback().then();
  };

  const sendFeedback = async () => {
    const payload = {
      body: request,
      feedback_topic_id: topic.id,
      lang,
      privacy_policy_accepted: checkboxValue,
    };

    if (!isAuth) {
      payload.name = name;
      payload.email = email;
    }

    const data = await sendRequest({
      request: Api.sendFeedback,
      payload,
      warnErrorText: 'while sending feedback',
      showErrorInModal: false,
      setLoading,
    });

    if (data?.status === 'ok') {
      openInfoModal({
        text: data.message,
      });
      setTopic('', true);
      setRequest('', true);
      setCheckboxValue(false, true);
      if (!isAuth) {
        setName('', true);
        setEmail('', true);
      }
    } else if (data?.data) {
      handleErrors(data.data);
    } else {
      openInfoModal({ text: data?.message || t(DICTIONARY.error) });
    }
  };

  const handleErrors = (data) => {
    if (Array.isArray(data?.name)) setNameError(data.name.join('. '));
    if (Array.isArray(data?.email)) setEmailError(data.email.join('. '));
    if (Array.isArray(data?.feedback_topic_id)) setTopicError(data.feedback_topic_id.join('. '));
    if (Array.isArray(data?.body)) setRequestError(data.body.join('. '));
  };

  const onCheckBoxClick = () => {
    if (checkboxValue) return setCheckboxValue(false, true);

    setModalState({
      isOpen: true,
      children: <PersonalDataProcessingModal onConfirm={confirmPolicy} />,
      containerStyle: { padding: 0 },
      withCloseButton: true,
      closeTimeoutMS: 0,
    });
  };

  const confirmPolicy = async () => {
    closeModal();
    await sleep(80);
    setCheckboxValue(true, true);
  };

  const inputs = [
    ...(!isAuth ? [{
      componentType: FORM_INPUT_TYPES.input,
      name: 'name',
      text: t(DICTIONARY.nameInputLabel),
      value: name,
      onChange: setName,
      isRequired: true,
      error: nameError,
    },
    {
      componentType: FORM_INPUT_TYPES.input,
      name: 'email',
      text: t(DICTIONARY.emailInputLabel),
      value: email,
      onChange: setEmail,
      isRequired: true,
      error: emailError,
    }] : []),
    {
      componentType: FORM_INPUT_TYPES.select,
      text: t(DICTIONARY.topicInputLabel),
      items: feedbackTopics,
      value: topic,
      placeholder: t(DICTIONARY.topicInputPlaceholder),
      onChange: setTopic,
      valueField: 'id',
      labelField: 'name',
      isRequired: true,
      error: topicError,
    },
    {
      componentType: FORM_INPUT_TYPES.input,
      text: t(DICTIONARY.requestInputLabel),
      value: request,
      onChange: setRequest,
      error: requestError,
      isTextArea: true,
      textAreaHeight: 115,
    },
    {
      componentType: FORM_INPUT_TYPES.checkbox,
      text: t(DICTIONARY.privacyPolicyCheckbox),
      value: checkboxValue,
      onClick: onCheckBoxClick,
      error: checkboxValueError,
    },
  ];

  const buttons = [
    {
      componentType: FORM_BUTTON_TYPES.main,
      text: t(DICTIONARY.submitButtonText),
      type: 'submit',
      onClick: () => {},
    },
  ];

  return (
    <Form
      title={t(DICTIONARY.formTitle)}
      inputs={inputs}
      buttons={buttons}
      onSubmit={onSubmit}
      source={BuildingImage}
      maxHeight={windowWidth * 0.48}
      {...p}
    />
  );
}

export default QuestionsForm;
